import { useState, useRef } from "react";
import { locations as locsApi, locationMaps } from "../lib/supabase.js";

const CORE_FIELDS = [
  { key: "name", label: "Name", required: true, aliases: ["name", "business_name", "company", "dealer_name", "location_name", "store_name", "center_name"] },
  { key: "address_line1", label: "Address", aliases: ["address", "address_line1", "street", "street_address", "addr"] },
  { key: "city", label: "City", aliases: ["city", "town"] },
  { key: "state_province", label: "State", aliases: ["state", "state_province", "province", "region", "st"] },
  { key: "postal_code", label: "Zip/Postal", aliases: ["zip", "zip_code", "postal_code", "zipcode", "postcode", "postal"] },
  { key: "country", label: "Country", aliases: ["country", "country_code"] },
  { key: "phone", label: "Phone", aliases: ["phone", "phone_number", "tel", "telephone"] },
  { key: "email", label: "Email", aliases: ["email", "email_address", "e_mail"] },
  { key: "website_url", label: "Website", aliases: ["website", "website_url", "url", "web", "site"] },
  { key: "latitude", label: "Latitude", aliases: ["latitude", "lat", "y"] },
  { key: "longitude", label: "Longitude", aliases: ["longitude", "lng", "lon", "long", "x"] },
  { key: "location_type", label: "Type", aliases: ["type", "location_type", "dealer_type", "category", "loc_type"] },
  { key: "description", label: "Description", aliases: ["description", "desc", "notes", "note"] },
];

const SKIP_COLUMNS = ["id", "created_at", "updated_at"]; // Auto-skip these

function autoMapColumns(headers) {
  const mapping = {};
  const normalized = headers.map((h) => h.toLowerCase().trim().replace(/\s+/g, "_"));
  for (const field of CORE_FIELDS) {
    const idx = normalized.findIndex((h) => field.aliases.includes(h));
    if (idx >= 0) mapping[headers[idx]] = field.key;
  }
  // Auto-skip known non-data columns
  for (const h of headers) { if (SKIP_COLUMNS.includes(h.toLowerCase().trim())) mapping[h] = ""; }
  return mapping;
}

function guessFieldType(values) {
  const sample = values.filter(Boolean).slice(0, 20);
  if (sample.length === 0) return "text";
  const boolVals = ["true", "false", "yes", "no", "1", "0", "y", "n"];
  if (sample.every((v) => boolVals.includes(v.toLowerCase()))) return "boolean";
  if (sample.every((v) => !isNaN(parseFloat(v)))) return "number";
  if (sample.every((v) => v.startsWith("http"))) return "url";
  const unique = [...new Set(sample)];
  if (unique.length <= 6 && sample.length >= 5) return "select";
  return "text";
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = (line) => { const r = []; let c = "", q = false; for (let i = 0; i < line.length; i++) { const ch = line[i]; if (ch === '"') { q = !q; continue; } if (ch === "," && !q) { r.push(c.trim()); c = ""; continue; } c += ch; } r.push(c.trim()); return r; };
  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((l) => { const v = parseLine(l); const r = {}; headers.forEach((h, i) => { r[h] = v[i] || ""; }); return r; }).filter((r) => Object.values(r).some((v) => v));
  return { headers, rows };
}

export default function CSVImporter({ map, color, onComplete, onClose }) {
  const [step, setStep] = useState("upload"); // upload | mapping | preview | importing | done
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [suggestedFields, setSuggestedFields] = useState([]); // auto-detected custom fields
  const [acceptedFields, setAcceptedFields] = useState(new Set());
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const existingCustomFields = map?.custom_fields || [];

  const allTargetOptions = [
    { value: "", label: "— Skip —" },
    ...CORE_FIELDS.map((f) => ({ value: f.key, label: f.label })),
    ...existingCustomFields.map((cf) => ({ value: `custom:${cf.key}`, label: `⚡ ${cf.label}` })),
  ];

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers: h, rows: r } = parseCSV(e.target.result);
      setHeaders(h); setRows(r);

      const autoMap = autoMapColumns(h);

      // Detect unmapped columns and suggest custom fields
      const suggested = [];
      for (const hdr of h) {
        if (autoMap[hdr]) continue; // Already mapped
        if (SKIP_COLUMNS.includes(hdr.toLowerCase().trim())) continue;
        const existingCf = existingCustomFields.find((cf) => cf.key === hdr.toLowerCase().replace(/\s+/g, "_"));
        if (existingCf) {
          autoMap[hdr] = `custom:${existingCf.key}`;
          continue;
        }
        // Suggest as new custom field
        const values = r.map((row) => row[hdr]);
        const fieldType = guessFieldType(values);
        const key = hdr.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
        const label = hdr.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const uniqueVals = [...new Set(values.filter(Boolean))];
        suggested.push({
          csvHeader: hdr, key, label, type: fieldType,
          options: fieldType === "select" ? uniqueVals.slice(0, 20) : [],
          sampleValues: values.filter(Boolean).slice(0, 3),
        });
        autoMap[hdr] = `new:${key}`;
      }

      setColumnMap(autoMap);
      setSuggestedFields(suggested);
      setAcceptedFields(new Set(suggested.map((s) => s.key))); // Accept all by default
      setStep("mapping");
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]); };

  const setMapping = (csvHeader, target) => setColumnMap((p) => ({ ...p, [csvHeader]: target }));

  const toggleAcceptField = (key) => {
    setAcceptedFields((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const buildLocationRows = () => {
    const acceptedCustom = suggestedFields.filter((f) => acceptedFields.has(f.key));
    return rows.map((row) => {
      const loc = { map_id: map.id, is_active: true, attributes: {} };
      for (const [csvHeader, target] of Object.entries(columnMap)) {
        if (!target || !row[csvHeader]) continue;
        if (target.startsWith("custom:")) {
          const cfKey = target.replace("custom:", "");
          const cf = existingCustomFields.find((f) => f.key === cfKey);
          let val = row[csvHeader];
          if (cf?.type === "boolean") val = ["true", "1", "yes", "y"].includes(val.toLowerCase());
          else if (cf?.type === "number") val = parseFloat(val) || 0;
          loc.attributes[cfKey] = val;
        } else if (target.startsWith("new:")) {
          const cfKey = target.replace("new:", "");
          if (!acceptedFields.has(cfKey)) continue;
          const sf = acceptedCustom.find((f) => f.key === cfKey);
          let val = row[csvHeader];
          if (sf?.type === "boolean") val = ["true", "1", "yes", "y"].includes(val.toLowerCase());
          else if (sf?.type === "number") val = parseFloat(val) || 0;
          loc.attributes[cfKey] = val;
        } else {
          let val = row[csvHeader];
          if (target === "latitude" || target === "longitude") val = parseFloat(val) || null;
          loc[target] = val;
        }
      }
      if (!loc.formatted_address) loc.formatted_address = [loc.address_line1, loc.city, loc.state_province, loc.postal_code].filter(Boolean).join(", ");
      return loc;
    }).filter((l) => l.name && (l.latitude || l.longitude));
  };

  const handleImport = async () => {
    setStep("importing");

    // First, add accepted new custom fields to the map config
    const acceptedNew = suggestedFields.filter((f) => acceptedFields.has(f.key));
    if (acceptedNew.length > 0) {
      const updatedFields = [
        ...existingCustomFields,
        ...acceptedNew.map((f) => ({ key: f.key, label: f.label, type: f.type, options: f.options })),
      ];
      await locationMaps.update(map.id, { custom_fields: updatedFields });
    }

    // Then bulk insert locations
    const locationRows = buildLocationRows();
    let imported = 0, errors = 0;
    for (let i = 0; i < locationRows.length; i += 50) {
      const batch = locationRows.slice(i, i + 50);
      const { error } = await locsApi.createMany(batch);
      if (error) { console.error("Batch error:", error); errors += batch.length; } else imported += batch.length;
    }

    setImportResult({ total: locationRows.length, imported, errors, skipped: rows.length - locationRows.length, newFields: acceptedNew.length });
    setStep("done");
  };

  const previewRows = rows.length > 0 ? buildLocationRows().slice(0, 5) : [];
  const totalValid = rows.length > 0 ? buildLocationRows().length : 0;
  const inputStyle = { padding: "7px 10px", background: "#0a0a10", border: "1px solid #1e1e2e", borderRadius: 6, color: "#ccc", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 14, padding: 28, width: 850, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Import CSV → {map?.name}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#555", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {/* Progress */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {["upload", "mapping", "preview", "done"].map((s, i) => (
            <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: ["upload", "mapping", "preview", "importing", "done"].indexOf(step) >= i ? color : "#1a1a2a" }} />
          ))}
        </div>

        {/* Upload */}
        {step === "upload" && (
          <div onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = color; }} onDragLeave={(e) => { e.currentTarget.style.borderColor = "#1e1e2e"; }} onDrop={handleDrop} onClick={() => fileRef.current?.click()} style={{ padding: "60px 20px", border: "2px dashed #1e1e2e", borderRadius: 12, textAlign: "center", cursor: "pointer" }}>
            <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.3 }}>📄</div>
            <div style={{ fontSize: 14, color: "#888" }}>Drop a CSV file here or click to browse</div>
            <input ref={fileRef} type="file" accept=".csv,.tsv" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} style={{ display: "none" }} />
          </div>
        )}

        {/* Mapping */}
        {step === "mapping" && (
          <>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 14 }}>
              <strong style={{ color: "#ccc" }}>{rows.length}</strong> rows, <strong style={{ color: "#ccc" }}>{headers.length}</strong> columns detected.
            </div>

            {/* Column mapping table */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "8px 12px", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: "#555", fontWeight: 700, textTransform: "uppercase" }}>CSV Column</div><div /><div style={{ fontSize: 10, color: "#555", fontWeight: 700, textTransform: "uppercase" }}>Maps To</div>
              {headers.map((h) => (
                <div key={h} style={{ display: "contents" }}>
                  <div style={{ padding: "6px 10px", background: "#0a0a10", borderRadius: 4, fontSize: 12, color: "#ccc", fontFamily: "monospace" }}>
                    {h}<span style={{ display: "block", fontSize: 10, color: "#444", fontFamily: "inherit", marginTop: 2 }}>e.g. "{rows[0]?.[h]?.toString().slice(0, 30) || "—"}"</span>
                  </div>
                  <span style={{ color: "#333" }}>→</span>
                  <select value={columnMap[h] || ""} onChange={(e) => setMapping(h, e.target.value)} style={{ ...inputStyle, width: "100%", cursor: "pointer" }}>
                    {allTargetOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    {suggestedFields.filter((s) => s.csvHeader === h).map((s) => <option key={`new:${s.key}`} value={`new:${s.key}`}>🆕 New: {s.label} ({s.type})</option>)}
                  </select>
                </div>
              ))}
            </div>

            {/* New custom fields detected */}
            {suggestedFields.length > 0 && (
              <div style={{ padding: 14, background: "#0a1a2e", border: "1px solid #1e3a5f", borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "#60a5fa", fontWeight: 700, marginBottom: 8 }}>🆕 New Custom Fields Detected</div>
                <div style={{ fontSize: 11, color: "#8bb4d9", marginBottom: 10 }}>These CSV columns don't match any existing fields. We'll create them as custom fields on this map.</div>
                {suggestedFields.map((f) => (
                  <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #1e3a5f" }}>
                    <input type="checkbox" checked={acceptedFields.has(f.key)} onChange={() => toggleAcceptField(f.key)} style={{ cursor: "pointer" }} />
                    <span style={{ fontSize: 12, color: "#ccc", fontWeight: 600, fontFamily: "monospace", width: 120 }}>{f.key}</span>
                    <span style={{ fontSize: 11, color: "#888" }}>{f.label}</span>
                    <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 10, background: "#1a2a3a", color: "#60a5fa" }}>{f.type}</span>
                    <span style={{ fontSize: 10, color: "#555" }}>e.g. {f.sampleValues.slice(0, 2).join(", ")}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button onClick={() => setStep("upload")} style={{ padding: "10px 20px", background: "#1a1a2a", border: "1px solid #2a2a3a", borderRadius: 8, color: "#888", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
              <button onClick={() => setStep("preview")} style={{ padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Preview Import →</button>
            </div>
          </>
        )}

        {/* Preview */}
        {step === "preview" && (
          <>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 14 }}>
              <strong style={{ color: "#ccc" }}>{totalValid}</strong> valid locations
              {rows.length - totalValid > 0 && <span style={{ color: "#f59e0b" }}> ({rows.length - totalValid} skipped)</span>}
              {suggestedFields.filter((f) => acceptedFields.has(f.key)).length > 0 && <span style={{ color: "#60a5fa" }}> + {suggestedFields.filter((f) => acceptedFields.has(f.key)).length} new custom field{suggestedFields.filter((f) => acceptedFields.has(f.key)).length > 1 ? "s" : ""} will be created</span>}
            </div>
            <div style={{ overflowX: "auto", marginBottom: 20 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead><tr style={{ borderBottom: "1px solid #1a1a2a" }}>
                  {["Name", "Address", "Lat/Lng", "Type"].map((h) => <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: "#555", fontWeight: 700, fontSize: 10, textTransform: "uppercase" }}>{h}</th>)}
                </tr></thead>
                <tbody>{previewRows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #1a1a2a" }}>
                    <td style={{ padding: "8px 10px", color: "#ccc" }}>{r.name}</td>
                    <td style={{ padding: "8px 10px", color: "#888" }}>{r.formatted_address?.slice(0, 40)}</td>
                    <td style={{ padding: "8px 10px", color: "#888", fontFamily: "monospace", fontSize: 10 }}>{r.latitude?.toFixed?.(4)}, {r.longitude?.toFixed?.(4)}</td>
                    <td style={{ padding: "8px 10px", color: "#888" }}>{r.location_type}</td>
                  </tr>
                ))}</tbody>
              </table>
              {totalValid > 5 && <div style={{ fontSize: 11, color: "#444", padding: "8px 10px" }}>...and {totalValid - 5} more</div>}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button onClick={() => setStep("mapping")} style={{ padding: "10px 20px", background: "#1a1a2a", border: "1px solid #2a2a3a", borderRadius: 8, color: "#888", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
              <button onClick={handleImport} style={{ padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Import {totalValid} Locations</button>
            </div>
          </>
        )}

        {step === "importing" && <div style={{ textAlign: "center", padding: "40px 0" }}><div style={{ fontSize: 24, marginBottom: 12 }}>⟳</div><div style={{ fontSize: 14, color: "#888" }}>Importing...</div></div>}

        {step === "done" && importResult && (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 16, color: "#22c55e", fontWeight: 700, marginBottom: 8 }}>Import Complete</div>
            <div style={{ fontSize: 13, color: "#888", lineHeight: 1.8 }}>
              <div><strong style={{ color: "#ccc" }}>{importResult.imported}</strong> locations imported</div>
              {importResult.newFields > 0 && <div><strong style={{ color: "#60a5fa" }}>{importResult.newFields}</strong> new custom field{importResult.newFields > 1 ? "s" : ""} added to map</div>}
              {importResult.skipped > 0 && <div><strong style={{ color: "#f59e0b" }}>{importResult.skipped}</strong> rows skipped</div>}
              {importResult.errors > 0 && <div><strong style={{ color: "#f87171" }}>{importResult.errors}</strong> errors</div>}
            </div>
            <button onClick={() => { onComplete?.(); onClose(); }} style={{ marginTop: 20, padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
